import React, { useEffect, useMemo, useState } from 'react';
import { fetchDirectory } from '../../utils/membersApi';
import './Members.css';

const initials = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('');

const MemberDirectory = ({ onEditProfile }) => {
  const [state, setState] = useState({ loading: true, members: [], error: null });
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchDirectory()
      .then((members) => !cancelled && setState({ loading: false, members, error: null }))
      .catch((error) => !cancelled && setState({ loading: false, members: [], error }));
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return state.members;
    return state.members.filter((m) =>
      [m.name, m.jobRole, m.interests, m.location].some((v) => (v || '').toLowerCase().includes(q)));
  }, [query, state.members]);

  return (
    <div className="members-directory">
      <div className="members-directory-intro">
        <p>
          Want to appear here? Open your profile and tick <strong>"Show me in the member directory"</strong>.
          Your photo, name, job role, location, interests and email are only visible to logged-in EBTA members.
        </p>
        <button type="button" className="members-secondary-btn" onClick={onEditProfile}>
          Edit my directory profile
        </button>
      </div>

      {state.loading && <p className="members-status">Loading...</p>}

      {state.error && (
        <p className="members-status members-status-error">
          {state.error.status === 503
            ? 'The member directory is not available yet. Please check back soon.'
            : state.error.status === 401
              ? 'Your session has expired. Please log in again.'
              : 'The member directory could not be loaded right now. Please try again later.'}
        </p>
      )}

      {!state.loading && !state.error && (
        <>
          <input
            type="search"
            className="members-search"
            placeholder="Search by name, role, interest or location"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {filtered.length === 0 && (
            <p className="members-status">
              {state.members.length === 0 ? 'No members are listed yet.' : 'No members match your search.'}
            </p>
          )}
          <div className="members-directory-grid">
            {filtered.map((m) => (
              <div key={m.uid} className="members-card">
                {m.imageUrl
                  ? <img className="members-card-photo" src={m.imageUrl} alt={m.name} loading="lazy" />
                  : <div className="members-card-photo members-card-initials" aria-hidden="true">{initials(m.name)}</div>}
                <h4 className="members-card-name">{m.name}</h4>
                {m.jobRole && <p className="members-card-role">{m.jobRole}</p>}
                {m.location && <p className="members-card-location">📍 {m.location}</p>}
                {m.interests && <p className="members-card-interests">{m.interests}</p>}
                {m.email && <a className="members-card-email" href={`mailto:${m.email}`}>{m.email}</a>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MemberDirectory;
